import UAParser from 'ua-parser-js';
import { FacingMode } from './types';
import { facingMode } from './constants';

const cameraLowResHeight = 1080;
const cameraLowResWidth = 1920;

const mobileCameraResWidth = 2560;
const mobileCameraResHeight = 1440;

export function getUserAgent(): string | null | undefined {
  const userAgent = typeof window !== 'undefined' && navigator && window.navigator.userAgent;
  return userAgent || undefined;
}

export function parseUserAgent() {
  // This maybe evaluated in next.js Node rather than in Browser
  if (!getUserAgent()) {
    return undefined;
  }
  const parser = new UAParser();
  return parser.getResult();
}

export function isMobileUA(): boolean {
  const result = parseUserAgent();
  if (!result || !result.device || !result.device.type) {
    return false;
  }
  return ['mobile', 'tablet'].includes(result.device.type);
}

export function isFirefoxUA() {
  const result = parseUserAgent();
  return result?.browser?.name === 'Firefox';
}

export function isSafariUA() {
  const result = parseUserAgent();
  return result?.browser?.name === 'Safari';
}

export function isChromeUA() {
  const result = parseUserAgent();
  return result?.browser?.name === 'Chrome';
}

export function isSamsungUA() {
  const result = parseUserAgent();
  return result?.device?.vendor === 'Samsung';
}

export function isAndroid() {
  const result = parseUserAgent();
  return result?.os?.name === 'Android';
}

/**
 * See https://developer.chrome.com/docs/multidevice/user-agent/#webview_user_agent
 * This onloy detects the WebView after Android 4.4. It'd return false for older
 * Android version.
 */
export function isAndroidWebView() {
  const result = parseUserAgent();
  // If the user-agent string contains "wv", 'ua-parser-js' will return the
  // string "WebView" as part of its browser name.
  return result?.os?.name === 'Android' && /WebView/.test(String(result?.browser?.name));
}

export function isAndroid12OrAbove() {
  console.log('Above Android OS 12');
  return getMajorAndroidVersion() >= 12;
}

export function isIOSUA() {
  const result = parseUserAgent();
  return result?.os?.name === 'iOS';
}

export function getMajorAndroidVersion(): number {
  const result = parseUserAgent();
  const matched = result?.os?.name === 'Android' ? String(result?.os?.version || '').match(/^\d+/) : null;
  return matched ? parseInt(matched[0], 10) : NaN;
}

export function getMajorSafariVersion(): number {
  const result = parseUserAgent();
  const name = result?.browser?.name;
  if (name === 'Safari' || name === 'Mobile Safari') {
    return parseInt(result?.browser?.version || '', 10);
  }
  return NaN;
}

export function getMajorChromeVersion(): number {
  const result = parseUserAgent();
  return result?.browser?.name === 'Chrome' ? parseInt(result?.browser?.version || '', 10) : NaN;
}

export function getMajorIOSVersion(): number {
  const result = parseUserAgent();
  const matched = result?.os?.name === 'iOS' ? String(result?.os?.version || '').match(/^\d+/) : null;
  return matched ? parseInt(matched[0], 10) : NaN;
}

export function getMajorMinorOsVersion() {
  const result = parseUserAgent();
  const version = String(result?.os?.version || '');
  const matched = version.match(/(^\d+)(\.\d+)?/);
  return (matched && matched[0]) || 'unknown';
}

export function isWindows(): boolean {
  return getPlatform() === 'desktop' && parseUserAgent()?.os?.name === 'Windows';
}

export function isMac(): boolean {
  return getPlatform() === 'desktop' && parseUserAgent()?.os?.name === 'Mac OS';
}

export function isLinux(): boolean {
  const ua = parseUserAgent();
  return ua?.os?.name === 'Linux';
}

// This returns the "platform"- iphone vs android vs desktop used for reporting metrics.
// Do not make this into something with high cardinality- that will impact SignalFX.
// If you need to alter/add to these values, please make sure to update the relevant
// SignalFX metrics as well.
export function getPlatform(): string {
  if (isAndroid()) {
    return 'android';
  }
  if (isIOSUA()) {
    return 'iphone';
  }
  return 'desktop';
}

export function isMobileDevice(): boolean {
  return isMobileUA() || isAndroidDesktop() || isIOSDesktop();
}

// Whether it's an IOS browser requesting desktop site.
export function isIOSDesktop(): boolean {
  // IOS browser => Request Desktop Site.
  return isMac() && window.navigator.maxTouchPoints > 1;
}

export function isIOS(): boolean {
  return isIOSUA() || isIOSDesktop();
}

export function isIOS15OrGreater(): boolean {
  if (isIOSDesktop()) {
    // In the past, the version of Safari has been in sync with the version of
    // iOS. Therefore, we could use Safari's version as a proxy to determine
    // the version of iOS. However, it's not possible to determine the iOS
    // version using other types of browsers.
    return getMajorSafariVersion() >= 15;
  }
  // iOS Mobile.
  return getMajorIOSVersion() >= 15;
}

// Whether it's an Android browser requesting desktop site.
export function isAndroidDesktop(): boolean {
  // Android browser => Request Desktop Site.
  return isLinux() && window.navigator.maxTouchPoints > 1;
}

const BACK_CAMERA_WORDS = [
  'rear',
  'back',
  'rück',
  'arrière',
  'trasera',
  'trás',
  'traseira',
  'posteriore',
  '后面',
  '後面',
  '背面',
  '后置', // alternative
  '後置', // alternative
  '背置', // alternative
  'задней',
  'الخلفية',
  '후',
  'arka',
  'achterzijde',
  'หลัง',
  'baksidan',
  'bagside',
  'sau',
  'bak',
  'tylny',
  'takakamera',
  'belakang',
  'אחורית',
  'πίσω',
  'spate',
  'hátsó',
  'zadní',
  'darrere',
  'zadná',
  'задня',
  'stražnja',
  'belakang',
  'बैक',
];

const isBackCameraLabel = (label: string): boolean => {
  const lowerCase = label.toLowerCase();
  return BACK_CAMERA_WORDS.some((keyword) => lowerCase.includes(keyword));
};

export const getCameraList = async (backOnly: boolean): Promise<Array<any>> => {
  try {
    if (!navigator.mediaDevices) {
      return [];
    }
    let cameraList = await navigator.mediaDevices
      .enumerateDevices()
      .then((devices) =>
        devices
          .filter((device) => device.kind === 'videoinput' && (!backOnly || isBackCameraLabel(device.label)))
          .map((device) => ({ deviceId: device.deviceId, label: device.label })),
      );
    if (isAndroid()) {
      cameraList = cameraList.sort((a, b) => (a.label < b.label ? -1 : 1));
    }
    return cameraList;
  } catch (error: any) {
    // handleException(error, 'error listing cameras');
    return [];
  }
};

export const getVideoConstraints = (
  availableDevices?: Array<any> | null,
  faceMode?: FacingMode,
  requireHD?: boolean,
  isDocumentScan?: boolean,
) => {
  // Use 1.5 ratio. US DL are 1.58, passport are 1.42, so take something in the middle
  console.log('Get Video Constraint devices:', availableDevices);
  let deviceId = availableDevices[0];
  if (isIOS() && isDocumentScan && availableDevices?.length > 2) {
    const ultraWideDevice = availableDevices.find(({ label }) => label.includes('Ultra Wide'));
    deviceId = ultraWideDevice || availableDevices[0];
  }

  let resizeMode = 'crop-and-scale';
  if (isMobileDevice()) {
    // Known cases that 'crop-and-scale' does not work.
    if (
      // Chrome 107+ on Android shows broken video in 'crop-and-scale'.
      (isAndroid() && getMajorChromeVersion() >= 107) ||
      // Samsung video driver has a bug when using resizeMode
      (isSamsungUA() && !isAndroid12OrAbove())
    ) {
      resizeMode = 'none';
    }

    return {
      audio: false,
      video: {
        facingMode: faceMode,
        resizeMode,
        width: faceMode === FacingMode.environment ? { ideal: mobileCameraResWidth } : { ideal: cameraLowResWidth },
        // height: { exact: 1080 },
        deviceId: deviceId ? deviceId.deviceId : undefined,
        aspectRatio: 1.7777777778,
        focusMode: 'continuous',
      },
    };
  }
  return {
    audio: false,
    video: {
      aspectRatio: 1.5,
      // deviceId: deviceId ? deviceId.deviceId : undefined,
      height: { min: cameraLowResHeight },
      resizeMode,
    },
  };
};
